import React, { useMemo, useState, useCallback } from "react";
import { Modal as SemanticModal, Input, Segment, Grid } from "semantic-ui-react";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { validateTitle } from "utils";
import { useDictionary } from "hooks";
import { addListItem, toggleListItemActive } from "store/actions/tenant";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import Modal from "@cayuse/shared-react.components.modal";
import Toggle from "@cayuse/shared-react.components.form.inputs.toggle";

import styles from "./styles.css";


const ResolutionsList = ({
	instanceResolutions: resolutions,
	instanceDictionary,
	instance
}) => {
	const MODAL_ID = "addNewResolutionModal";
	const dictionary = useDictionary("resolutions");
	const [ isLoading, setIsLoading ] = useState(false);
	const [ doesTitleExist, setDoesTitleExist ] = useState(false);
	const [ isTitleUnique, setIsTitleUnique ] = useState(true);
	const [ addResolutionModalOpen, setAddResolutionModalOpen ] = useState(false);
	const [ resolutionValue, setResolutionValue ] = useState("");


	const handleOpen = () => {
		setAddResolutionModalOpen(true);
	};

	const handleClose = () => {
		setIsTitleUnique(true);
		setDoesTitleExist(false);
		setResolutionValue("");
		setAddResolutionModalOpen(false);
	};

	const validate = useCallback((title) => {
		const { trimmedTitle, isTitleNotEmpty, isSameTitleNotPresent } = validateTitle(title, resolutions);
		setDoesTitleExist(isTitleNotEmpty);

		if (isTitleNotEmpty) {
			setIsTitleUnique(isSameTitleNotPresent);

			if (isSameTitleNotPresent) {
				setResolutionValue(trimmedTitle);
			}
		}
	}, [ resolutions ]);

	const handleSave = useCallback(() => {
		if (!isNonEmptyString(resolutionValue)) {
			return;
		}

		setIsLoading(true);
		addListItem({
			value: resolutionValue,
			active: true,
			hash: resolutionValue
		}, "resolutions",instance)
			.finally(()=>{
				setIsLoading(false);
				handleClose();
			});
	}, [ resolutionValue ]);

	const handleCancel = () => {
		setIsTitleUnique(true);
		setDoesTitleExist(false);
		handleClose();
	};

	const resolutionRows = useMemo(() => {
		const activeResolutions = resolutions.filter(resolution => resolution.active);
		const isOneResolutionActive = activeResolutions.length === 1;
		const clickHandler = resolution => {
			if (!(isOneResolutionActive && resolution.active)) {
				toggleListItemActive(resolution.hash, "resolutions",instance);
			}
		};

		return (Array.isArray(resolutions)) ? (resolutions
			.map(resolution => {
				const activeAriaText = resolution.active ? "active" : "not active";

				return (
					<Grid.Row key={resolution.hash}>
						<Grid.Column>{resolution.value}</Grid.Column>
						<Grid.Column>
							<Toggle
								aria-label={`${resolution.value} is ${activeAriaText}`}
								active={resolution.active}
								// TODO: Add component level dictionary
								dictionary={dictionary.components.toggle}
								disabled={isOneResolutionActive && resolution.active}
								onClick={() => clickHandler(resolution)}
							/>
						</Grid.Column>
					</Grid.Row>
				)})) : [];
	}, [ resolutions, dictionary ]);


	const addNewResolutionButton = (
		<IconButton
			icon="plus"
			labelPosition="left"
			primary
			onClick={handleOpen}
			aria-controls={MODAL_ID}
		>
			{dictionary.addResolution || "Add New Resolution"}
		</ IconButton>
	);

	// TODO: Revisit this use of useMemo, it may not be helpful with so many dependencies
	const addResolutionModal = useMemo(() => (
		<Modal closeIcon
			id={MODAL_ID}
			trigger={addNewResolutionButton}
			closeOnEscape={true}
			onClose={handleClose}
			open={addResolutionModalOpen}
			ariaLabel={dictionary.modalHeader || "Create a new resolution."}
		>
			<SemanticModal.Header
				className={`${styles.modalHeader} whiteOnBlue`}
			>
				<h2>{dictionary.modalHeader || "Create a new resolution."}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				{
					dictionary.modalContent ||
                    `Enter a title for your new resolution and click "Save", 
                    your new resolution will be added to the list and set to active.`
				}
				<VerticalSpacing />
				{dictionary.modalGuidance || `Click "cancel" to discard changes and return to list`}
				<VerticalSpacing />
				<Segment className={styles.modalSegment}>
					<Grid columns="equal">
						<Grid.Row className={styles.modalContentRow}>
							<Grid.Column width="7">
								<Input
									type="text"
									aria-label={dictionary.inputAriaLabel || `Resolution`}
									autoFocus
									placeholder={dictionary.inputPlaceholder || `Resolution...`}
									onChange={({ target: { value } }) => validate(value)}
								/>
							</Grid.Column>
							<Grid.Column verticalAlign="middle" className={styles.validationMessagesWrapper}>
								<span className={styles.validationMessage} hidden={doesTitleExist}>
									{dictionary.errorNoTitle || "Title must not be empty or all whitespace."}
								</span>
								<span className={styles.validationMessage} hidden={isTitleUnique}>
									{dictionary.errorDuplicateTitle || "Title must be unique."}
								</span>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Segment>
				<VerticalSpacing />
			</SemanticModal.Content>
			<SemanticModal.Actions>
				<ButtonGroup>
					<Button secondary onClick={handleCancel}>
						{dictionary.cancel || "Cancel"}
					</Button>
					<Button primary
						onClick={handleSave}
						disabled={!doesTitleExist || !isTitleUnique || isLoading}
						busy={isLoading}
						loadingMessage="Save in process"
					>
						{dictionary.saveResolution || "Save"}
					</Button>
				</ButtonGroup>
			</SemanticModal.Actions>
		</Modal>
	), [ addNewResolutionButton, addResolutionModalOpen, dictionary.modalHeader, dictionary.modalContent, dictionary.modalGuidance, dictionary.inputPlaceholder, dictionary.errorNoTitle, dictionary.errorDuplicateTitle, dictionary.cancel, dictionary.saveResolution, doesTitleExist, isTitleUnique, handleCancel, handleSave, validate, resolutionValue ]);

	return (
		<div id={styles.resolutions}>
			<Segment attached="top" className={styles.headerSegment}>
				<h3>{instanceDictionary.resolutionContentHeader || "Closed Proposal Resolutions"}</h3>
			</Segment>
			<Segment attached="bottom">
				<div className={styles.description}>
					{instanceDictionary.resolutionGuidance}
				</div>
				<Segment attached="top" className={styles.gridHeaderContainer}>
					<Grid columns="equal" padded="horizontally">
						<Grid.Row divided>
							<Grid.Column className={`${styles.leftHeaderColumn} ${styles.columnHeader}`}>
								<h4>{dictionary.titleRowHeader || "Resolution Title"}</h4>
							</Grid.Column>
							<Grid.Column className={styles.columnHeader}>
								<h4>{dictionary.activeRowHeader || "Active Status"}</h4>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Segment>
				<Segment attached="bottom">
					<Grid columns="equal" className={styles.resolutionsGrid}>
						{resolutionRows}
					</Grid>
				</Segment>
				{addResolutionModal}
			</Segment>
		</div>
	);
};

export default ResolutionsList;
